import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable, skip, Subject } from "rxjs";
import { AppState } from "../../../store/reducers";
import {
  distinctUntilChanged,
  filter,
  map,
  takeUntil,
  withLatestFrom,
} from "rxjs/operators";
import { ActivatedRoute, Router } from "@angular/router";
import { AssetTreeComponent } from "../asset-tree/asset-tree.component";
import { AssetSelectedAction } from "../../../store/actions/dashboard.actions";
import { selectSelectedAssetId } from "../../../store/selectors/dashboard.selectors";
import { AsyncPipe } from "@angular/common";

@Component({
  selector: "ngx-asset-tree-sidebar",
  templateUrl: "./asset-tree-sidebar.component.html",
  styleUrls: ["./asset-tree-sidebar.component.scss"],
  imports: [AssetTreeComponent, AsyncPipe],
})
export class AssetTreeSidebarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  assetTreeLoading$: Observable<boolean>;
  disableIssueFilters$: Observable<boolean>;

  @ViewChild(AssetTreeComponent, { static: true })
  assetTreeComponent: AssetTreeComponent;

  constructor(
    private readonly store: Store<AppState>,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  private destroyed$ = new Subject<void>();

  ngOnInit() {
    this.assetTreeLoading$ = this.store.pipe(
      select((state: AppState) => !state.dashboard.assetTreeLoaded),
    );

    this.disableIssueFilters$ = this.store.pipe(
      select(
        (state: AppState) => state.dashboard.issuesState.disableIssueFilters,
      ),
    );
  }

  ngAfterViewInit() {
    const queryParams$ = this.activatedRoute.queryParams.pipe(skip(1));
    const queryParamAssetId$ = queryParams$.pipe(
      map((params) => parseInt(params.assetId, 10)),
      distinctUntilChanged(),
      map((assetId) => (isNaN(assetId) ? null : assetId)),
    );

    this.selectAssetByQueryParam$(queryParamAssetId$);
    this.ensureUrlHasQueryParam$(queryParamAssetId$);
  }

  private selectAssetByQueryParam$(queryParamAssetId$: Observable<number>) {
    queryParamAssetId$
      .pipe(
        filter((assetId) => assetId !== null),
        takeUntil(this.destroyed$),
      )
      .subscribe((assetId) => {
        this.store.dispatch(new AssetSelectedAction({ assetId }));
      });
  }

  private ensureUrlHasQueryParam$(queryParamAssetId$: Observable<number>) {
    queryParamAssetId$
      .pipe(
        filter((assetId) => assetId === null),
        withLatestFrom(this.store.select(selectSelectedAssetId)),
        takeUntil(this.destroyed$),
      )
      .subscribe(([_, selectedAssetId]) => {
        this.setAssetIdForCurrentUrl(selectedAssetId);
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  setAssetIdForCurrentUrl(assetId: number) {
    this.router
      .navigate([], {
        queryParams: { assetId },
        queryParamsHandling: "merge",
      })
      .then();
  }
}
